import { format, parse } from 'date-fns';
import { nl } from 'date-fns/locale';
import docReady from '../../../assets/js/docReady';

class EnergyFeedInCostCalculator {
    constructor(element) {
        this.calculatorTool = element;
        this.init();

        this.feedInCosts = window.feedInCosts;
        this.feedInOverviewOnPage = window.feedInOverviewOnPage;
        this.providerFeedInCosts = {};
        this.hasCalculated = false;
    }

    init() {
        this.setupElements();
        this.attachEventListeners();
    }

    setupElements() {
        this.provider = this.calculatorTool.querySelector('select[name="provider"]');
        this.feedIn = this.calculatorTool.querySelector('input[name="feed-in"]');
        this.usage = this.calculatorTool.querySelector('input[name="usage"]');
        this.calculateButton = this.calculatorTool.querySelector('.btn');

        // Results:
        this.resultsElement = this.calculatorTool.querySelector('.results');
        this.costsSpan = this.resultsElement.querySelector('.total-costs');
        this.explanationElement = this.resultsElement.querySelector('.explanation');
        this.detailsLinkContainer = this.resultsElement.querySelector('.details-link');
        this.detailsLink = this.detailsLinkContainer.querySelector('a');
        this.missedCashbackElement = this.resultsElement.querySelector('.missed-cashback');
    }

    attachEventListeners() {
        this.calculateButton.addEventListener('click', this.calculate.bind(this));
        this.provider.addEventListener('change', this.calculateAfterFirstCalculation.bind(this));

        this.feedIn.addEventListener('change', () => {
            // this.clearResults.bind(this);
            this.calculateAfterFirstCalculation();
        });
        this.usage.addEventListener('change', () => {
            // this.clearResults.bind(this);

            this.calculateAfterFirstCalculation();
        });
    }

    calculateAfterFirstCalculation() {
        if (this.hasCalculated) {
            this.calculate();
        }
    }

    calculate() {
        const feedIn = this.feedIn.value;
        const usage = this.usage.value;
        let settledFeedIn = feedIn;
        if (usage > 0) {
            settledFeedIn = Math.min(feedIn, usage);
        }

        // Calculate feedin costs for every provider:
        this.providerFeedInCosts = {};

        for (const [key, value] of Object.entries(this.feedInCosts)) {
            let calculationFeedIn = feedIn;
            let costs = 0;
            let missedCashback = 0;
            let hasTiers = false;
            let hasMultipleTiers = false;
            let hasFixedFeedInCosts = false;
            let hasExtraFixedCosts = false;

            if (value.calculation === 'settledFeedIn') {
                calculationFeedIn = settledFeedIn;
            }

            if (value.kwhCosts !== null) {
                costs += calculationFeedIn * value.kwhCosts;
            }

            // Loop through tiers:
            if (value.tiers !== null) {
                hasTiers = value.tiers.length > 0;
                hasMultipleTiers = value.tiers.length > 1;

                for (const tier of value.tiers) {
                    if ((calculationFeedIn >= tier.min || tier.min == null) && (calculationFeedIn <= tier.max || tier.max == null)) {
                        if (tier.feedInCosts !== null) {
                            costs += tier.feedInCosts;
                            hasFixedFeedInCosts = true;
                        }
                        if (tier.extraFixedCosts !== null) {
                            costs += tier.extraFixedCosts;
                            hasExtraFixedCosts = true;
                        }
                        if (tier.missedCashback !== null) {
                            missedCashback += tier.missedCashback;
                        }
                    }
                }
            }

            this.providerFeedInCosts[key] = {
                costs,
                missedCashback,
                hasTiers,
                hasMultipleTiers,
                hasFixedFeedInCosts,
                hasExtraFixedCosts,
                calculationMethod: value.calculation,
            };
        }

        this.hasCalculated = true;
        this.displayResults();
    }

    displayResults() {
        this.resultsElement.classList.remove('initial');
        this.resultsElement.classList.add('update-results');

        // Find costs for selected provider:
        const providerCosts = this.providerFeedInCosts[this.provider.value];
        const providerName = this.feedInCosts[this.provider.value].name;
        const { date } = this.feedInCosts[this.provider.value];
        const { isFuture } = this.feedInCosts[this.provider.value];

        this.costsSpan.innerHTML = `€ ${providerCosts.costs.toFixed(2)
            .replace('.', ',')}`;

        let hasMissedCashback = false;
        if (providerCosts.missedCashback > 0) {
            // this.missedCashbackElement.classList.remove('hidden');
            if (providerCosts.costs === 0) {
                this.missedCashbackElement.innerHTML = `Hoewel je bij ${providerName} geen terugleverkosten betaalt, loop je gemiddeld <strong>€ ${Math.round(providerCosts.missedCashback)}</strong> aan welkomstbonus mis wanneer je zonnepanelen hebt.`;
            } else {
                this.missedCashbackElement.innerHTML = `Daarnaast loop je bij ${providerName} gemiddeld <strong>€ ${Math.round(providerCosts.missedCashback)}</strong> aan welkomstbonus mis wanneer je zonnepanelen hebt.`;
            }
            hasMissedCashback = true;
        } else {
            this.missedCashbackElement.innerHTML = '';
            // this.missedCashbackElement.classList.add('hidden');
        }

        // Explanation
        let explanation = '';
        let dateExplanation = '';

        if (date !== null && isFuture) {
            const parsedDate = parse(date, 'yyyy-MM-dd', new Date());
            dateExplanation = `vanaf ${format(parsedDate, 'd MMMM yyyy', { locale: nl })}`;
        }

        if (providerCosts.costs === 0 && !hasMissedCashback) {
            explanation = `Hoewel je geen terugleverkosten betaalt en geen welkomstkorting misloopt, is ${providerName} niet aan te raden. <br> Een dynamisch energiecontract is vaak minder voordelig met zonnepanelen. Lees hier hoe een <a href="/nieuws/dynamisch-energie-met-zonnepanelen">dynamisch energiecontract met zonnepanelen</a> werkt.`;
        } else if (providerCosts.costs > 0) {
            const { kwhCosts } = this.feedInCosts[this.provider.value];

            if (!providerCosts.hasTiers) {
                explanation = `Bij ${providerName} betaal je ${dateExplanation} een vast bedrag van ${(kwhCosts * 100).toFixed(1)
                    .replace('.', ',')} cent per kWh teruggeleverde energie.`;
            } else if (!providerCosts.hasMultipleTiers && kwhCosts > 0) {
                explanation = `Bij ${providerName} betaal je ${dateExplanation} een vast bedrag van ${(kwhCosts * 100).toFixed(1)
                    .replace('.', ',')} cent per kWh teruggeleverde energie, en daarnaast extra vastrecht.`;
            } else if (providerCosts.hasFixedFeedInCosts) {
                if (providerCosts.hasMultipleTiers) {
                    explanation = `${providerName} rekent ${dateExplanation} terugleverkosten in verschillende schalen.`;
                } else {
                    explanation = `${providerName} rekent ${dateExplanation} expliciete terugleverkosten aan klanten met zonnepanelen.`;
                }
            } else {
                explanation = `${providerName} rekent ${dateExplanation} extra vastrecht aan klanten met zonnepanelen.`;
            }

            if (providerCosts.calculationMethod === 'settledFeedIn') {
                explanation += ' Deze kosten zijn berekend over de stroom die je saldeert.';
            } else {
                explanation += ' Deze kosten worden berekend over alle stroom die je teruglevert.';
            }
        }

        if (explanation.length > 0) {
            this.explanationElement.innerHTML = explanation;
        } else {
            this.explanationElement.innerHTML = '';
        }

        // Link to details:
        if (this.feedInOverviewOnPage) {
            this.detailsLink.href = `#terugleverkosten-${this.provider.value}`;
        } else {
            this.detailsLink.href = `/energie/terugleverkosten#terugleverkosten-${this.provider.value}`;
        }
        this.detailsLinkContainer.classList.remove('hidden');

        this.resultsElement.classList.remove('no-results');
        this.resultsElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });

        setTimeout(() => {
            this.resultsElement.classList.remove('update-results');
        }, 500);
    }

    clearResults() {
        this.resultsElement.classList.add('hide-results');
        this.costsSpan.innerHTML = '€ -,--';
        this.detailsLinkContainer.classList.add('hidden');

        setTimeout(() => {
            this.resultsElement.classList.remove('hide-results');

            // this.explanationElement.classList.add('hidden');
            // this.missedCashbackElement.classList.add('hidden');
            this.explanationElement.innerHTML = '';
            this.missedCashbackElement.innerHTML = '';
            if (this.feedInOverviewOnPage) {
                this.detailsLink.href = '#';
            } else {
                this.detailsLink.href = '/energie/terugleverkosten';
            }

            this.resultsElement.classList.add('no-results');
        }, 500);
    }
}

// Initialize the tool when the document is ready
docReady(() => {
    const elements = document.querySelectorAll('.feed-in-calculator-widget');
    elements.forEach((element) => new EnergyFeedInCostCalculator(element));
});
